import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, AlertCircle, Wrench, Filter } from 'lucide-react';

export default function MaintenanceListPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filters, setFilters] = useState({ priority: 'ALL', status: 'ALL', houseId: 'ALL' });
  const [formData, setFormData] = useState({
    house_id: '',
    title: '',
    description: '',
    priority: 'MEDIUM'
  });

  useEffect(() => {
    fetchRequests();
    fetchHouses();
  }, [filters]);

  const fetchRequests = async () => {
    try {
      const params = {};
      if (filters.priority && filters.priority !== 'ALL') params.priority = filters.priority;
      if (filters.status && filters.status !== 'ALL') params.status = filters.status;
      if (filters.houseId && filters.houseId !== 'ALL') params.houseId = filters.houseId;
      const response = await api.get('/maintenance', { params });
      setRequests(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchHouses = async () => {
    try {
      const response = await api.get('/houses');
      setHouses(response.data.items || []);
    } catch (error) {
      console.error('Failed to load houses');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/maintenance', formData);
      toast.success('Maintenance request created successfully');
      setShowCreateDialog(false);
      setFormData({ house_id: '', title: '', description: '', priority: 'MEDIUM' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create request');
    }
  };

  const handleResolve = async (requestId) => {
    if (!confirm('Mark this maintenance request as resolved?')) return;
    try {
      await api.post(`/maintenance/${requestId}/resolve`);
      toast.success('Request marked as resolved');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resolve request');
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      LOW: 'secondary',
      MEDIUM: 'default',
      HIGH: 'warning',
      URGENT: 'destructive'
    };
    return <Badge variant={variants[priority] || 'default'}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      OPEN: 'destructive',
      IN_PROGRESS: 'warning',
      RESOLVED: 'default'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="maintenance-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Maintenance Register</h1>
          <p className="text-muted-foreground mt-1">Track and manage house maintenance issues</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-maintenance-button">
          <Plus className="h-4 w-4 mr-2" />
          Report Issue
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>House</Label>
              <Select value={filters.houseId} onValueChange={(value) => setFilters({ ...filters, houseId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All houses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All houses</SelectItem>
                  {houses.map((house) => (
                    <SelectItem key={house.id} value={house.id}>{house.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={filters.priority} onValueChange={(value) => setFilters({ ...filters, priority: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All priorities</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => setFilters({ priority: 'ALL', status: 'ALL', houseId: 'ALL' })}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>House</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reported By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id} data-testid={`maintenance-row-${request.id}`}>
                <TableCell className="font-medium">{request.house_name || 'N/A'}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{request.title}</div>
                    {request.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">{request.description}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>{getPriorityBadge(request.priority)}</TableCell>
                <TableCell>{getStatusBadge(request.status)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{request.reported_by_name || 'N/A'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(request.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  {request.status !== 'RESOLVED' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResolve(request.id)}
                      data-testid={`resolve-${request.id}`}
                    >
                      Resolve
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {requests.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            <Wrench className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No maintenance requests found</p>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-maintenance-dialog">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Report Maintenance Issue</DialogTitle>
              <DialogDescription>Create a new maintenance request</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="house_id">House *</Label>
                <Select
                  value={formData.house_id}
                  onValueChange={(value) => setFormData({ ...formData, house_id: value })}
                  required
                >
                  <SelectTrigger id="house_id">
                    <SelectValue placeholder="Select house" />
                  </SelectTrigger>
                  <SelectContent>
                    {houses.filter(h => h.status === 'ACTIVE').map((house) => (
                      <SelectItem key={house.id} value={house.id}>{house.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Issue Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Broken front door lock"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the issue in detail"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit">Create Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
